export interface DeviceFingerprintData {
  web_id: string;
//   client_ip: string;
}

const STORAGE_KEY = 'rcl_device_id';

const getPublicIpAddress = async (): Promise<string> => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');

    if (!response.ok) {
      return '';
    }

    const data = await response.json();
    return typeof data?.ip === 'string' ? data.ip : '';
  } catch (error) {
    return '';
  }
};

// Generate a cryptographically random, collision-proof identifier.
// This is what guarantees two physically identical devices get DIFFERENT ids.
const generateRandomId = (): string => {
  if (crypto?.randomUUID) {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

// --- redundant persistence so the id survives partial storage clears ---

const readCookie = (name: string): string => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : '';
};

// Derive the registrable parent domain (e.g. "redcliffelabs.com" from
// "admin.redcliffelabs.com") so the cookie is shared across all subdomains.
// Falls back to host-only (empty) for localhost / IPs.
const getRootDomain = (): string => {
  const host = window.location.hostname;
  if (host === 'localhost' || /^[\d.]+$/.test(host)) {
    return '';
  }
  const parts = host.split('.');
  if (parts.length <= 2) {
    return host;
  }
  return parts.slice(-2).join('.');
};

const writeCookie = (name: string, value: string): void => {
  // 10 years
  const maxAge = 60 * 60 * 24 * 365 * 10;
  const rootDomain = getRootDomain();
  const domainAttr = rootDomain ? `; domain=.${rootDomain}` : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax${domainAttr}`;
};

// Read the persisted device id from any available store, or create + persist a new one.
const getOrCreatePersistentId = (): string => {
  let id = '';

  try {
    id = localStorage.getItem(STORAGE_KEY) || '';
  } catch (e) {
    /* storage blocked */
  }

  if (!id) {
    try {
      id = sessionStorage.getItem(STORAGE_KEY) || '';
    } catch (e) {
      /* storage blocked */
    }
  }

  if (!id) {
    id = readCookie(STORAGE_KEY);
  }

  if (!id) {
    id = generateRandomId();
  }

  // write back to every store so a single cleared store can self-heal from the others
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch (e) {
    /* ignore */
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, id);
  } catch (e) {
    /* ignore */
  }
  writeCookie(STORAGE_KEY, id);

  return id;
};

const getDeviceFingerprint = async (): Promise<DeviceFingerprintData> => {
  // The random persistent id is the sole source of identity. It is already
  // collision-proof (crypto-random UUID) and shared across all
  // *.redcliffelabs.com dashboards via the cookie, so the web_id is identical
  // across dashboards on the same browser, and different across devices.
  //
  // NOTE: we deliberately do NOT mix in device/browser fingerprint components
  // (userAgent, canvas, WebGL, salt, ...). Those add no uniqueness over the
  // random UUID and would make web_id diverge between apps whenever any single
  // component differs (e.g. a different REACT_APP_DEVICE_FINGERPRINT_SALT per
  // deployment), which is exactly the bug where the same device got different
  // web_ids on crmsales vs partner.
  const persistentId = getOrCreatePersistentId();

  const encoder = new TextEncoder();
  const data = encoder.encode(persistentId);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

//   const client_ip = await getPublicIpAddress();

  return {
    web_id: hashHex.substring(0, 32),
    // client_ip
  };
};

export default getDeviceFingerprint;
